<template>
  <v-container>
    <v-overlay
      :model-value="overlay"
      class="align-center justify-center"
    >
      <div class="text-center">
        <v-progress-circular
          color="primary"
          size="64"
          indeterminate
        ></v-progress-circular>
        <div class="mt-4 text-h6 text-white">
          Processing. Large batch data processing time may exceed 1 minute.
        </div>
      </div>
    </v-overlay>

    <v-stepper alt-labels :items="stepsName" hide-actions :model-value="stepId">
      <!-- step1: Upload Data -->
      <template v-slot:[`item.1`]>
        <v-card flat>
          <v-card-title class="d-flex align-center">
            <span class="text-subtitle-1 text-sm-body-2">Upload Data &nbsp;</span>
            <v-icon class="mr-2">mdi mdi-help-circle</v-icon>
            <v-btn
              variant="text"
              color="primary"
              @click="showDataFormatDialog = true"
              class="pa-0"
              style="text-transform: none; font-size: 0.875rem; min-width: auto; letter-spacing: normal;"
            >
              See data format
            </v-btn>
          </v-card-title>
          <v-file-input
            v-model="dataFiles"
            clearable
            label="Data files (supports multiple files)"
            variant="solo-filled"
            show-size
            multiple
            accept=".xlsx,.xls,.csv,.txt"
            prepend-icon="mdi-file-upload"
            :rules="fileRules"
            class="text-subtitle-1"
            @change="handleDataFileChange"
            @update:model-value="handleDataFileChange">
          </v-file-input>
          
          <!-- 文件信息展示区域 -->
          <div v-if="fileInfoList && fileInfoList.length > 0" class="file-info-section mt-3">
            <v-card variant="outlined" class="pa-3">
              <div class="text-subtitle-2 font-weight-medium mb-3">
                Selected Files ({{ fileInfoList.length }})
              </div>
              
              <div v-for="(fileInfo, index) in fileInfoList" :key="index" class="file-item" :class="{ 'mb-3': index < fileInfoList.length - 1 }">
                <div class="d-flex align-center">
                  <v-avatar size="40" class="mr-3">
                    <v-icon size="24" :color="getFileIconColor(fileInfo.type)">
                      {{ getFileIcon(fileInfo.name) }}
                    </v-icon>
                  </v-avatar>
                  
                  <div class="flex-grow-1">
                    <div class="text-subtitle-2 font-weight-medium">{{ fileInfo.name }}</div>
                    <div class="text-caption text-medium-emphasis">
                      {{ formatFileSize(fileInfo.size) }} • {{ getFileTypeText(fileInfo.name) }}
                    </div>
                  </div>
                  
                  <div class="ml-3">
                    <v-icon v-if="fileUploadStatus === 'success'" color="success" size="24">
                      mdi-check-circle
                    </v-icon>
                    <v-icon v-else-if="fileUploadStatus === 'error'" color="error" size="24">
                      mdi-alert-circle
                    </v-icon>
                    <v-progress-circular 
                      v-else-if="fileUploadStatus === 'uploading'" 
                      indeterminate 
                      color="primary" 
                      size="24"
                    ></v-progress-circular>
                    <v-icon v-else color="info" size="24">
                      mdi-file-check
                    </v-icon>
                  </div>
                </div>
              </div>
              
              <div v-if="fileUploadStatus === 'uploading'" class="mt-3">
                <v-progress-linear 
                  indeterminate 
                  color="primary"
                  height="4"
                ></v-progress-linear>
                <div class="text-caption text-center mt-1 text-medium-emphasis">
                  Processing {{ fileInfoList.length }} files...
                </div>
              </div>
              
              <div v-if="fileUploadStatus === 'success' && convertResult" class="mt-2">
                <v-chip size="small" color="success" variant="tonal">
                  <v-icon start>mdi-check</v-icon>
                  Successfully converted {{ convertResult.converted_count }}/{{ convertResult.total_count }} IDs
                </v-chip>
              </div>
              
              <div v-if="fileUploadStatus === 'error'" class="mt-2">
                <v-chip size="small" color="error" variant="tonal">
                  <v-icon start>mdi-alert</v-icon>
                  Upload failed
                </v-chip>
              </div>
            </v-card>
          </div>
          
          <!-- 分隔符和输入框 -->
          <v-row align="start" class="mt-4">
            <v-col cols="auto" class="d-flex align-center">
              <span class="separator-text">or</span>
            </v-col>
            <v-col>
              <v-textarea
                v-model="userInputIds"
                label="Enter IDs"
                placeholder="Enter vocabulary, one per line, e.g.:&#10;OMIM:181500&#10;OMIM:273300&#10;7440-48-4"
                variant="solo-filled"
                rows="4"
                clearable
                hide-details
                class="text-subtitle-1"
                @input="handleIdsInput"
              ></v-textarea>
            </v-col>
          </v-row>

          <div class="d-md-flex align-center justify-space-between mb-2">
            <div class="d-flex flex-wrap align-center gap-8">
              <v-checkbox
                v-model="useExampleData"
                label="Try example data"
                hide-details
                @change="handleExampleDataChange"
              ></v-checkbox>
              <v-checkbox
                v-model="includeChemicalName"
                label="Including chemical name"
                hide-details
                @change="handleIncludeChemicalNameChange"
                class="ml-16"
              ></v-checkbox>
            </div>

            <div class="d-flex flex-wrap gap-2 mt-2 mt-md-0 justify-end">
              <v-btn
                color="primary"
                variant="outlined"
                size="small"
                prepend-icon="mdi-download"
                class="mr-2"
                @click="downloadExampleFile('txt')"
              >
                txt
              </v-btn>
              <v-btn
                color="primary"
                variant="outlined"
                size="small"
                prepend-icon="mdi-download"
                @click="downloadExampleFile('csv')"
              >
                csv
              </v-btn>
              <v-btn
                color="primary"
                variant="outlined"
                size="small"
                prepend-icon="mdi-download"
                class="ml-2"
                @click="downloadExampleFile('xlsx')"
              >
                xlsx
              </v-btn>
            </div>
          </div>
        </v-card>
        
        <!-- 数据格式说明对话框 -->
        <v-dialog v-model="showDataFormatDialog" max-width="900" persistent scrollable>
          <v-card>
            <v-card-title class="text-h6 d-flex align-center">
              How to Use the Database Search
              <v-spacer></v-spacer>
              <v-btn @click="showDataFormatDialog = false" size="small" variant="text" class="square-close-btn">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text style="max-height: 70vh; overflow-y: auto;">
              <div class="text-body-2 data-format-content" style="line-height: 1.7;">
                <p>This function (E-DB) allows you to explore the more specific links in "Exposure-Biology-Disease" network relationships by querying curated biological databases. You can start a search by pasting IDs into the input box or uploading a text file. Each line should contain one identifier. You may mix different types of identifiers in the same search, e.g.:</p>
                <v-card variant="outlined" class="pa-3 my-3 bg-grey-lighten-4">
                  <pre style="margin: 0; font-family: monospace; font-size: 13px;">
OMIM:181500
OMIM:273300
7440-48-4
TP53
BRCA1
PRODH
80-05-7
7157
MESH:D003920
KEGG:hsa01210
KEGG:hsa04520
GO:0008219
GO:0004657</pre>
                </v-card>
                <p><strong>Note:</strong> If you enter mixed types of identifiers, the system will focus on their relationship, rather than their other related links. Selecting more databases may increase result coverage but also increase complexity. Please check whether your input IDs are recognized in the stage of "Tidy".</p>

                <h3 class="text-h6 mt-4 mb-2">What Can I Enter?</h3>
                <p>The E-DB automatically recognizes the type of each input and links it to the corresponding biological entities. Please see the typical identifiers as below:</p>

                <h4 class="text-subtitle-1 font-weight-bold mt-3">(1) Exposure (Environmental Exposure / Chemicals)</h4>
                <p>They can be queried using mixed chemical identifiers commonly used in environmental and toxicological studies. Users can search the detailed information using <a href="https://pubchem.ncbi.nlm.nih.gov" target="_blank">PubChem</a>. Supported identifiers include:</p>
                <ul>
                  <li><strong>CAS numbers</strong> - uniquely identify chemical substances (e.g., 80-05-7, 7440-48-4)</li>
                  <li><strong>SMILES</strong> (e.g., CC(C)(C1=CC=C(C=C1)O)C2=CC=C(C=C2)O)</li>
                  <li><strong>InChIKey</strong> (e.g., IISBACLAFKSPIT-UHFFFAOYSA-N)</li>
                  <li><strong>Chemical names</strong> (e.g., Bisphenol A)</li>
                </ul>
                <p>Additionally, the platform defines a set of internally assigned special exposure identifiers and their corresponding exposures as follows:</p>
                <ul>
                  <li>EX:E0001S (tobacco smoking)</li>
                  <li>EX:E0002S (alcohol consumption)</li>
                  <li>EX:E0003S (mental stress)</li>
                  <li>EX:E0004S (NOₓ exposure)</li>
                  <li>EX:E0005S (PM₂.₅ exposure)</li>
                  <li>EX:E0006S (sleep deprivation)</li>
                  <li>EX:E0007S (heat exposure)</li>
                  <li>EX:E0008S (high-sodium diet)</li>
                  <li>EX:E0009S (UV radiation)</li>
                  <li>EX:E0010S (physical exercise)</li>
                  <li>EX:E0011S (high-fat diet)</li>
                  <li>EX:E0012S (COVID-19)</li>
                  <li>EX:E0013S (noise pollution)</li>
                  <li>EX:E0014S (traffic-related air pollution)</li>
                </ul>

                <h4 class="text-subtitle-1 font-weight-bold mt-3">(2) Disease</h4>
                <p>Disease entities represent human diseases and health outcomes curated from authoritative biomedical databases. They can be queried using OMIM IDs or MeSH IDs. Users can search using <a href="https://www.omim.org" target="_blank">OMIM</a> and <a href="https://www.ncbi.nlm.nih.gov/mesh" target="_blank">MeSH</a>.</p>
                <ul>
                  <li><strong>OMIM identifiers</strong> - must be entered with the prefix "OMIM:" (e.g., OMIM:181500, OMIM:605543)</li>
                  <li><strong>MeSH identifiers</strong> - must be entered with the prefix "MeSH:" (e.g., MeSH:D003920, MeSH:D012141)</li>
                </ul>

                <h4 class="text-subtitle-1 font-weight-bold mt-3">(3) Gene</h4>
                <p>Gene entities represent human genes using official gene symbols following the HGNC standard, or alternatively, the unique NCBI Gene IDs.</p>
                <ul>
                  <li><strong>HGNC Official Symbol</strong> (e.g., TP53, BRCA1)</li>
                  <li><strong>NCBI Gene IDs</strong> (e.g., 7157 for TP53, 672 for BRCA1)</li>
                </ul>

                <h4 class="text-subtitle-1 font-weight-bold mt-3">(4) Protein</h4>
                <p>Protein entities can be queried using multiple standardized identifier types:</p>
                <ul>
                  <li><strong>Protein names</strong> - refer to the protein products encoded by genes (e.g., TP53, BRCA1)</li>
                  <li><strong>Ensembl Protein IDs</strong> - formatted as ENSP########### (e.g., ENSP00000269305, ENSP00000350283)</li>
                  <li><strong>UniProt accession numbers</strong> - provide stable references with comprehensive annotations (e.g., P04637, Q9Y243, O14920)</li>
                </ul>

                <h4 class="text-subtitle-1 font-weight-bold mt-3">(5) Biological Pathway</h4>
                <p>Biological pathway entities can be queried using pathway names or database-specific pathway identifiers curated from sources like KEGG or Reactome.</p>
                <ul>
                  <li><strong>Full official names</strong> (e.g., p53 signaling pathway, p53-dependent g1 dna damage response)</li>
                  <li><strong>Database-specific identifiers</strong> (e.g., KEGG:hsa04115, REACT:R69563)</li>
                </ul>

                <h4 class="text-subtitle-1 font-weight-bold mt-3">(6) Gene Ontology (GO)</h4>
                <p>GO entities represent standardized functional annotations describing biological processes, molecular functions, and cellular components. They can be queried using GO term IDs, which follow the format GO:####### (e.g., GO:0006954). Users can search the detailed information using <a href="https://www.ebi.ac.uk/QuickGO/" target="_blank">QuickGO</a>.</p>

                <h3 class="text-h6 mt-4 mb-2">Choosing Databases</h3>
                <p>In the stage of "Choose Database", you can select which relationship databases to search. Each database represents a specific type of biological relationship, such as:</p>
                <ol>
                  <li>Exposure - Gene</li>
                  <li>Exposure - Protein</li>
                  <li>Gene - Disease</li>
                  <li>Protein - Disease</li>
                  <li>Pathway - Disease</li>
                </ol>

                <h3 class="text-h6 mt-4 mb-2">How the Search Works</h3>
                <p>The system first identifies the node type of each input (chemical, gene, disease, etc.). It then retrieves all related biological links from the selected databases, which can be integrated and displayed as a network using the next function (<a href="https://www.exposomex.cn/ebio" target="_blank">E-BIO</a>).</p>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Chemical name processing notice -->
        <v-dialog v-model="showChemicalNameDialog" max-width="520">
          <v-card>
            <v-card-title class="text-h6 d-flex align-center">
              Notice
              <v-spacer></v-spacer>
              <v-btn @click="showChemicalNameDialog = false" size="small" variant="text" class="square-close-btn">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              Processing may take up to about 8 minutes because chemical names were entered. Thank you for your patience~
            </v-card-text>
          </v-card>
        </v-dialog>
      </template>

      <!-- step2: Tidy -->
      <template v-slot:[`item.2`]>
        <v-card flat>
          <v-card-title class="d-flex align-center text-subtitle-1 text-sm-body-2">
            <span>Tidy Data</span>
            <v-spacer></v-spacer>
            <v-btn
              size="small"
              color="primary"
              variant="tonal"
              prepend-icon="mdi-download"
              @click="downloadTidyData"
              :disabled="!selected || selected.length === 0"
            >
              Download Current Data
            </v-btn>
          </v-card-title>
          <v-text-field
            v-model="searchCondition"
            label="Search"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            hide-details
            single-line
          ></v-text-field>
          <v-row style="height: 37px"></v-row>
          <v-row justify="center">
            <v-col cols="12">
              <div style="width: 100%; margin: 0 auto;">
                <v-data-table
                  v-model="selected"
                  :headers="vocaHeaders"
                  :items="vocaRowData"
                  :search="searchCondition"
                  :items-per-page="vocaItemsPerPage"
                  show-select
                  item-value="id"
                  :fixed-header="true"
                  :height="400"
                  v-model:sort-by="vocaSortBy"
                  must-sort
                  class="tidy-data-table"
                  style="border: 1px solid #212121;border-radius: 4px"
                  @update:model-value="onSelectionChanged">

                  <template v-for="header in vocaHeaders" :key="header.key" v-slot:[`header.${header.key}`]="{ column, getSortIcon }">
                    <div class="d-flex align-center">
                      <span>{{ header.title }}</span>
                      <v-icon
                        v-if="header.sortable !== false"
                        :icon="getSortIcon(column)"
                        size="16"
                        class="ml-1 sort-icon"
                      ></v-icon>
                    </div>
                  </template>

                  <template v-for="header in vocaHeaders" :key="header.key" v-slot:[`item.${header.key}`]="{ item }">
                    <span
                      :class="header.key === 'preferred_name' ? 'cell-ellipsis' : ''"
                      :title="header.key === 'preferred_name' ? item[header.key] : ''"
                      v-html="highlightText(item[header.key], searchCondition)"
                    ></span>
                  </template>
                </v-data-table>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </template>

      <!-- step3: Choose Database -->
      <template v-slot:[`item.3`]>
        <v-card flat>
          <v-row>
              <!-- Left Column: Database List -->
              <v-col cols="12" md="3">
                <v-card variant="outlined" style="height: 600px; overflow-y: auto;">
                  <v-card-title class="text-subtitle-2 pa-3 d-flex align-center">
                    <v-icon class="mr-2" color="primary">mdi-database</v-icon>
                    Select Databases
                  </v-card-title>
                  <v-divider></v-divider>
                  <!-- Select All Checkbox -->
                  <v-list-item class="select-all-item" @click="toggleSelectAll">
                    <template v-slot:prepend>
                      <v-checkbox
                        v-model="selectAllDatabases"
                        hide-details
                        density="compact"
                        :indeterminate="isIndeterminate"
                        @click.stop="toggleSelectAll"
                      ></v-checkbox>
                    </template>
                    <v-list-item-title class="text-body-2 font-weight-bold">
                      Select All
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ selectedDatabases.length }}/{{ availableDatabases.length }} selected
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list density="compact">
                    <v-list-item
                      v-for="db in availableDatabases"
                      :key="db.id"
                      :active="currentPreviewDatabase === db.id"
                      @click="previewDatabase(db.id)"
                      class="database-list-item"
                    >
                      <template v-slot:prepend>
                        <v-checkbox
                          :model-value="selectedDatabases.includes(db.id)"
                          hide-details
                          density="compact"
                          @click.stop="toggleDatabaseSelection(db.id)"
                        ></v-checkbox>
                      </template>
                      <v-list-item-title class="text-body-2 font-weight-medium">
                        {{ db.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        {{ db.description }}
                      </v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-col>

              <!-- Right Column: Data Preview -->
              <v-col cols="12" md="9">
                <v-card variant="outlined" style="height: 600px;">
                  <v-card-title class="d-flex align-center pa-3">
                    <v-icon class="mr-2" color="primary">mdi-table-eye</v-icon>
                    <span class="text-subtitle-2">
                      {{ currentPreviewDatabaseName }} - Data Preview
                    </span>
                    <v-spacer></v-spacer>
                    <v-btn
                      size="small"
                      color="primary"
                      variant="tonal"
                      prepend-icon="mdi-download"
                      @click="downloadPreviewData"
                      :disabled="!previewData.length"
                    >
                      Download Current Data
                    </v-btn>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-alert
                    v-if="previewLoading"
                    type="info"
                    variant="tonal"
                    class="ma-3"
                  >
                    Querying, please wait for a while.
                  </v-alert>
                  <v-card-text class="pa-0" style="height: calc(600px - 65px); overflow: hidden; display: flex; flex-direction: column;">
                    <v-data-table
                      :headers="previewHeaders"
                      :items="previewData"
                      :items-per-page="vocaItemsPerPage"
                      density="compact"
                      fixed-header
                      class="preview-table"
                      v-model:sort-by="previewSortBy"
                      must-sort
                    >
                      <template v-for="header in previewHeaders" :key="header.key" v-slot:[`header.${header.key}`]="{ column, getSortIcon }">
                        <div class="d-flex align-center">
                          <span>{{ header.title }}</span>
                          <v-icon
                            v-if="header.sortable !== false"
                            :icon="getSortIcon(column)"
                            size="16"
                            class="ml-1 sort-icon"
                          ></v-icon>
                        </div>
                      </template>
                      <template v-for="header in previewHeaders" :key="header.key" v-slot:[`item.${header.key}`]="{ item }">
                        <span
                          :class="isEllipsisField(header.key) ? 'cell-ellipsis' : ''"
                          :title="isEllipsisField(header.key) ? formatCellValue(item[header.key]) : ''"
                        >
                          {{ formatCellValue(item[header.key]) }}
                        </span>
                      </template>
                      <template v-slot:no-data>
                        <div class="text-center pa-8">
                          <v-icon size="64" color="grey-lighten-1">mdi-database-search</v-icon>
                          <p class="text-body-2 text-grey mt-4">
                            {{ previewLoaded ? 'No data returned for current selection' : 'Select a database to preview data' }}
                          </p>
                        </div>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>
              </v-col>
          </v-row>
        </v-card>
      </template>

      <!-- step4: Summary -->
      <template v-slot:[`item.4`]>
        <v-card flat>
          <v-card variant="outlined" class="pa-4">
            <div class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center gap-3 mb-4">
              <div>
                <div class="text-subtitle-1 font-weight-medium">Summary Status</div>
                <div class="text-caption text-medium-emphasis">
                  Builds all databases and packages results into a ZIP file
                </div>
              </div>
              <v-btn
                color="primary"
                variant="tonal"
                prepend-icon="mdi-download"
                @click="downloadSummaryZip"
                :disabled="!summaryReady"
                class="summary-btn"
              >
                Download All
              </v-btn>
            </div>

            <v-alert
              v-if="summaryLoading"
              type="info"
              variant="tonal"
              class="mb-4"
            >
              Summary is running, please wait for a while.
            </v-alert>

            <v-list density="compact" class="summary-list pa-0">
              <v-list-item v-for="db in selectedDatabasesList" :key="db.id">
                <template v-slot:prepend>
                  <v-icon color="primary" size="small">mdi-database</v-icon>
                </template>
                <v-list-item-title class="text-body-2">
                  {{ db.name }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ getDatabaseDescription(db.id) }}
                </v-list-item-subtitle>
                <template v-slot:append>
                  <v-btn
                    variant="tonal"
                    :color="getSummaryStatusColor(getModeStatus(db.id))"
                    class="text-body-2 summary-btn"
                  >
                    {{ getModeStatus(db.id) }}
                  </v-btn>
                </template>
              </v-list-item>
            </v-list>

            <v-alert
              v-if="selectedDatabases.length === 0"
              type="warning"
              variant="tonal"
              class="mt-4"
            >
              No databases selected. Please go back to Step 3 and select at least one database.
            </v-alert>
          </v-card>

          <!-- Success Message -->
          <v-alert v-if="downloadSuccess" type="success" variant="tonal" class="mt-4">
            <v-icon start>mdi-check-circle</v-icon>
            Summary file download started!
          </v-alert>
        </v-card>
      </template>

      <!-- Stepper navigation buttons -->
      <div class="stepper-actions">
        <v-divider class="mx-4 my-4"></v-divider>
        <div class="step-content">
          <v-spacer></v-spacer>
          <v-btn
            :disabled="stepId == 1"
            class="mr-4"
            size="large"
            variant="tonal"
            color="primary"
            @click="stepperPre"
          >
            Prev
          </v-btn>
          <v-btn
            :disabled="!canProceedToNext || loading"
            size="large"
            variant="tonal"
            color="primary"
            @click="stepperNext"
          >
            <span v-if="loading">Loading...</span>
            <span v-else>{{ isLastStep ? 'Exit' : 'Next' }}</span>
          </v-btn>
        </div>
        <!-- 超过10秒时显示的全局提示 -->
        <v-expand-transition>
          <div
            v-if="showLongProcessingAlert && loading && stepId === 1 && includeChemicalName"
            class="d-flex justify-center mt-4"
          >
            <v-sheet
              color="#7BA4DB"
              rounded="sm"
              class="pa-3 d-flex align-center text-white"
            >
              <v-icon class="mr-2">mdi-clock-outline</v-icon>
              Processing may take up to about 8 minutes because chemical names were entered. Thank you for your patience.
            </v-sheet>
          </div>
        </v-expand-transition>
      </div>
    </v-stepper>

    <!-- Summary 下载文件名弹窗 -->
    <v-dialog v-model="downloadDialogVisible" max-width="400">
      <v-card>
        <v-card-title>Download Files</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="downloadFileName"
            label="File Name"
            variant="outlined"
            density="compact"
            hide-details
          >
            <template v-slot:append-inner>
              <span class="text-grey">.zip</span>
            </template>
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="downloadDialogVisible = false">Cancel</v-btn>
          <v-btn color="primary" @click="confirmDownload" :loading="downloadLoading">
            Download
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Tidy 下载文件名弹窗 -->
    <v-dialog v-model="tidyDownloadDialogVisible" max-width="400">
      <v-card>
        <v-card-title>Download Tidy Data</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="tidyDownloadFileName"
            label="File Name"
            variant="outlined"
            density="compact"
            hide-details
          >
            <template v-slot:append-inner>
              <span class="text-grey">.csv</span>
            </template>
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="tidyDownloadDialogVisible = false">Cancel</v-btn>
          <v-btn color="primary" @click="confirmTidyDownload">
            Download
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Preview 下载文件名弹窗 -->
    <v-dialog v-model="previewDownloadDialogVisible" max-width="400">
      <v-card>
        <v-card-title>Download Preview Data</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="previewDownloadFileName"
            label="File Name"
            variant="outlined"
            density="compact"
            hide-details
          >
            <template v-slot:append-inner>
              <span class="text-grey">.csv</span>
            </template>
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="previewDownloadDialogVisible = false">Cancel</v-btn>
          <v-btn color="primary" @click="confirmPreviewDownload">
            Download
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';

export default {
  name: "EdbView",
  data() {
    return {
      overlay: false,
      stepId: 1,
      stepsName: ['Upload Data', 'Tidy', 'Choose Database', 'Summary'],
      loading: false,

      // Step 1: Upload Data
      dataFiles: null,
      dataFileExisted: false,
      fileInfoList: [],
      fileUploadStatus: 'idle',
      userInputIds: '',
      useExampleData: false,
      includeChemicalName: false,
      isUsingExampleData: false,  // 记录当前会话是否使用示例数据（在 Step 1 完成时设置）
      showDataFormatDialog: false,
      showChemicalNameDialog: false,
      convertResult: null,
      convertRequestId: 0,
      showLongProcessingAlert: false, // 超过10秒时显示提示
      fileRules: [
        value => {
          if (!value || value.length === 0) return true;
          const file = Array.isArray(value) ? value[0] : value;
          if (!file || !file.name) return 'Invalid file format';
          const allowedTypes = ['.xlsx', '.xls', '.csv', '.txt'];
          const fileName = file.name.toLowerCase();
          const isValid = allowedTypes.some(type => fileName.endsWith(type));
          return isValid || 'Only Excel (.xlsx, .xls), CSV (.csv) and TXT (.txt) files are supported';
        }
      ],
      exampleDataIds: [
      "OMIM:181500",
      "OMIM:273300",
      "7440-48-4",
      "TP53",
      "BRCA1",
      "PRODH",
      "80-05-7", 
      "7157",
      "MESH:D003920",
      "KEGG:hsa01210",
      "KEGG:hsa04520",
      "GO:0008219",
      "GO:0004657"
      ],

      // Step 2: Tidy Data
      searchCondition: '',
      vocaHeaders: [
        { title: 'Input ID', key: 'input_id', sortable: true },
        { title: 'EXID', key: 'exid', sortable: true },
        { title: 'Preferred Name', key: 'preferred_name', sortable: true },
        { title: 'Type', key: 'type', sortable: true },
        { title: 'Synonyms', key: 'synonyms', sortable: true }
      ],
      vocaRowData: [],
      vocaItemsPerPage: 10,
      vocaSortBy: [{ key: 'input_id', order: 'asc' }],
      selected: [],
      tidySelectedExidTypes: [],

      // Step 3: Choose Database
      selectedDatabases: [], // Multiple selection
      selectAllDatabases: true, // Select all checkbox
      currentPreviewDatabase: null,
      previewHeaders: [],
      previewData: [],
      previewLoaded: false,
      previewLoading: false,
      previewRequestId: 0,
      previewSortBy: [{ key: 'source', order: 'asc' }],
      availableDatabases: [
        { id: 'db_exposure_gene', name: 'DB_Exposure_Gene', description: 'relationship between exposure and gene expression' },
        { id: 'db_exposure_go', name: 'DB_Exposure_GO', description: 'relationship between exposure and GO (i.e. phenotype)' },
        { id: 'db_exposure_pathway', name: 'DB_Exposure_Pathway', description: 'relationship between exposure and pathway' },
        { id: 'db_exposure_protein', name: 'DB_Exposure_Protein', description: 'relationship between exposure and protein' },
        { id: 'db_gene_disease', name: 'DB_Gene_Disease', description: 'relationship between gene and disease' },
        { id: 'db_gene_protein', name: 'DB_Gene_Protein', description: 'relationship between gene and protein' },
        { id: 'db_go_disease', name: 'DB_GO_Disease', description: 'relationship between exposure and disease' },
        { id: 'db_pathway_disease', name: 'DB_Pathway_Disease', description: 'relationship between pathway and disease' },
        { id: 'db_protein_go', name: 'DB_Protein_GO', description: 'relationship between protein and GO' },
        { id: 'db_protein_disease', name: 'DB_Protein_Disease', description: 'relationship between protein and disease' },
        { id: 'db_protein_protein', name: 'DB_Protein_Protein', description: 'interactions between protein and protein' }
      ],
      buildModelModes: {
        db_exposure_gene: 'Exposure_Gene',
        db_exposure_go: 'Exposure_GO',
        db_exposure_pathway: 'Exposure_Pathway',
        db_exposure_protein: 'Exposure_Protein',
        db_gene_disease: 'Gene_Disease',
        db_gene_protein: 'Gene_Protein',
        db_go_disease: 'GO_Disease',
        db_pathway_disease: 'Pathway_Disease',
        db_protein_go: 'Protein_GO',
        db_protein_disease: 'Protein_Disease',
        db_protein_protein: 'Protein_Protein'
      },

      // Step 4: Summary
      downloadSuccess: false,
      summaryLoading: false,
      summaryModes: {},
      summaryReady: false,
      summaryRequestId: 0,
      // Summary 下载弹窗
      downloadDialogVisible: false,
      downloadFileName: '',
      downloadLoading: false,
      // Tidy 下载弹窗
      tidyDownloadDialogVisible: false,
      tidyDownloadFileName: '',
      // Preview 下载弹窗
      previewDownloadDialogVisible: false,
      previewDownloadFileName: ''
    };
  },
  computed: {
    ...mapGetters(['sessionUuid', 'sessionName']),
    isLastStep() {
      return this.stepId === this.stepsName.length;
    },
    hasValidInput() {
      const hasFiles = this.dataFileExisted && this.dataFiles && this.dataFiles.length > 0;
      const hasUserInput = this.userInputIds && this.userInputIds.trim().length > 0;
      const hasExampleData = this.useExampleData;
      return hasFiles || hasUserInput || hasExampleData;
    },
    canProceedToNext() {
      if (this.loading) return false;
      if (this.stepId === 1) {
        return this.hasValidInput;
      } else if (this.stepId === 2) {
        return this.selected && this.selected.length > 0;
      } else if (this.stepId === 3) {
        return true; // 允许不选数据库也能进入 Summary 页面
      } else if (this.stepId === 4) {
        return true;
      }
      return false;
    },
    currentPreviewDatabaseName() {
      const db = this.availableDatabases.find(d => d.id === this.currentPreviewDatabase);
      return db ? db.name : 'Database';
    },
    // 计算 Select All 的不确定状态
    isIndeterminate() {
      return this.selectedDatabases.length > 0 && this.selectedDatabases.length < this.availableDatabases.length;
    },
    // 获取选中的数据库列表（用于 Summary 显示）
    selectedDatabasesList() {
      return this.availableDatabases.filter(db => this.selectedDatabases.includes(db.id));
    },
    // 获取选中的数据库对应的模式列表
    selectedModes() {
      return this.selectedDatabases.map(dbId => this.buildModelModes[dbId]).filter(Boolean);
    }
  },
  async mounted() {
    console.log('E-DB组件挂载，初始化数据...');

    // 检查用户是否已登录
    const access_token = localStorage.getItem('access_token');
    if (!access_token) {
      this.$router.push('/login');
      return;
    }

    // 检查 URL 参数中是否有 session
    const urlParams = new URLSearchParams(window.location.search);
    const urlSession = urlParams.get('session');

    if (urlSession) {
      console.log('从 URL 参数中获取到 session:', urlSession);
      // 如果 URL 中有 session 参数，设置到 Vuex store 中
      this.$store.commit('SET_SESSION_UUID', urlSession);
      console.log('已将 URL session 设置到 store');

      // 获取会话信息，恢复到之前的步骤
      try {
        const response = await axios.post('/api/session/GetSessionInfo/', {
          access_token: access_token,
          session: urlSession
        });

        if (response.data.code === "200") {
          const sessionInfo = response.data.session_info;
          console.log('获取到会话信息:', sessionInfo);

          // 检查会话是否已完成 (status === "1")
          if (sessionInfo.status === "1" || sessionInfo.status === 1) {
            console.log('会话已完成，跳转到 Summary 页面');
            this.stepId = 4;
            this.$store.commit('SET_SESSION_STATUS', 1);
          } else {
            // 恢复到之前的步骤
            const savedStep = parseInt(sessionInfo.session_step) || 1;
            console.log('恢复到步骤:', savedStep);
            this.stepId = savedStep;
          }
        } else {
          console.log('获取会话信息失败，重置为步骤1');
          await this.updateSession({
            session_step: 1,
            status: 0
          });
        }
      } catch (sessionError) {
        console.warn('获取会话信息失败，重置为步骤1:', sessionError);
        await this.updateSession({
          session_step: 1,
          status: 0
        });
      }
    } else if (!this.sessionUuid) {
      console.log('URL 中没有 session 参数，且 store 中也没有 session，创建新会话...');
      // 如果没有 session，创建一个新的
      try {
        const sessionData = await this.createNewSession();
        console.log('新会话创建成功:', sessionData);

        // 更新 URL，添加 session 参数
        if (sessionData && sessionData.session) {
          const newUrl = `${window.location.pathname}?session=${sessionData.session}`;
          window.history.replaceState({}, '', newUrl);
          console.log('已更新 URL:', newUrl);
        }
      } catch (error) {
        console.error('创建新会话失败:', error);
        // 如果创建失败，跳转回首页
        this.$router.push('/start-all');
        return;
      }
    }

    console.log('Session UUID:', this.sessionUuid);
    console.log('Session Name:', this.sessionName);

    // 默认全选所有数据库
    this.selectedDatabases = this.availableDatabases.map(db => db.id);
    this.selectAllDatabases = true;
  },
  methods: {
    ...mapActions(['createSession', 'updateSession']),

    // 创建新会话
    async createNewSession() {
      try {
        console.log('开始创建新会话...');

        // 检查用户是否已登录
        const access_token = localStorage.getItem('access_token');
        if (!access_token) {
          throw new Error('用户未登录');
        }

        // 调用 Vuex action 创建会话
        const sessionData = await this.createSession({
          module_name: 'edb'
        });

        console.log('新会话创建成功:', sessionData);
        return sessionData;

      } catch (error) {
        console.error('创建新会话失败:', error);
        throw error;
      }
    },

    // Step 1 methods
    handleDataFileChange(event) {
      let files = null;
      if (event && event.target && event.target.files) {
        files = Array.from(event.target.files);
      } else if (Array.isArray(event)) {
        files = event;
      } else if (this.dataFiles) {
        files = Array.isArray(this.dataFiles) ? this.dataFiles : [this.dataFiles];
      }
      
      if (!files || files.length === 0) {
        this.dataFiles = null;
        this.dataFileExisted = false;
        this.fileInfoList = [];
        this.fileUploadStatus = 'idle';
        return;
      }
      
      this.dataFiles = files;
      this.dataFileExisted = true;
      this.fileInfoList = files.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type
      }));
      this.fileUploadStatus = 'ready';
      
      if (this.userInputIds) {
        this.userInputIds = '';
      }
      if (this.useExampleData) {
        this.useExampleData = false;
      }
    },

    handleIdsInput() {
      if (this.userInputIds && this.userInputIds.trim()) {
        if (this.dataFiles) {
          this.dataFiles = null;
          this.dataFileExisted = false;
          this.fileInfoList = [];
          this.fileUploadStatus = 'idle';
        }
        // 检测输入数据是否与 example 数据不同，如果不同就取消勾选 example
        if (this.useExampleData) {
          const exampleContent = this.exampleDataIds.join('\n');
          if (this.userInputIds.trim() !== exampleContent.trim()) {
            this.useExampleData = false;
          }
        }
      }
    },

    handleExampleDataChange() {
      if (this.useExampleData) {
        if (this.dataFiles) {
          this.dataFiles = null;
          this.dataFileExisted = false;
          this.fileInfoList = [];
          this.fileUploadStatus = 'idle';
        }
        this.userInputIds = this.exampleDataIds.join('\n');
      } else {
        this.userInputIds = '';
      }
    },

    handleIncludeChemicalNameChange() {
      if (this.includeChemicalName) {
        this.showChemicalNameDialog = true;
      }
    },

    async downloadExampleFile(fileType) {
      if (fileType === 'xlsx') {
        // xlsx 文件从后端 API 下载
        await this.downloadExcelFromAPI();
        return;
      }

      let content = '';
      let filename = '';
      let mimeType = '';

      if (fileType === 'txt') {
        content = this.exampleDataIds.join('\n');
        filename = 'example.txt';
        mimeType = 'text/plain';
      } else if (fileType === 'csv') {
        // 使用 Excel 公式格式，防止自动转换日期
        const csvRows = this.exampleDataIds.map(id => `"=""${String(id).replace(/"/g, '""')}"""`);
        content = 'ID\n' + csvRows.join('\n');
        filename = 'example.csv';
        mimeType = 'text/csv';
      }

      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    },

    // 从后端 API 下载 Excel 文件
    async downloadExcelFromAPI() {
      try {
        const response = await axios.post('/api/session/download_example_data/', {
          module: 'edb',
          type: 'excel'
        }, {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'example.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        console.log('已从后端下载Excel示例文件');
      } catch (error) {
        console.error('从后端下载Excel文件失败:', error);
        if (error.response && error.response.data) {
          try {
            const errorData = JSON.parse(new TextDecoder().decode(error.response.data));
            alert(`下载失败: ${errorData.message || '未知错误'}`);
          } catch {
            alert('下载Excel文件失败，请重试');
          }
        } else {
          alert('下载Excel文件失败，请重试');
        }
      }
    },

    getFileIcon(fileName) {
      const ext = fileName.toLowerCase().split('.').pop();
      const iconMap = {
        'xlsx': 'mdi-file-excel',
        'xls': 'mdi-file-excel',
        'csv': 'mdi-file-delimited',
        'txt': 'mdi-file-document'
      };
      return iconMap[ext] || 'mdi-file';
    },

    getFileIconColor(fileType) {
      if (fileType && fileType.includes('excel')) return 'success';
      if (fileType && fileType.includes('csv')) return 'info';
      return 'primary';
    },

    getFileTypeText(fileName) {
      const ext = fileName.toLowerCase().split('.').pop();
      const typeMap = {
        'xlsx': 'Excel file',
        'xls': 'Excel file',
        'csv': 'CSV file',
        'txt': 'Text file'
      };
      return typeMap[ext] || 'File';
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    // Step 2 methods
    // 格式化单元格值，将 "NA" 替换为空字符串
    formatCellValue(value) {
      if (value === null || value === undefined) return '';
      const strValue = String(value).trim();
      if (strValue === 'NA' || strValue === 'N/A' || strValue === 'na' || strValue === 'n/a') {
        return '';
      }
      return strValue;
    },

    highlightText(text, search) {
      // 先格式化值（处理 NA）
      const formattedText = this.formatCellValue(text);
      if (!search || !formattedText) return formattedText;
      const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedSearch})`, 'gi');
      return String(formattedText).replace(regex, '<mark>$1</mark>');
    },

    onSelectionChanged(selectedItems) {
      this.selected = this.normalizeSelectedIds(selectedItems);
    },

    normalizeSelectedIds(selectedItems) {
      if (!Array.isArray(selectedItems)) {
        return [];
      }
      if (selectedItems.length === 0) {
        return [];
      }
      if (typeof selectedItems[0] !== 'object') {
        return selectedItems;
      }
      return selectedItems
        .map(item => item?.id ?? item?.value ?? item?.raw?.id)
        .filter(id => typeof id === 'string');
    },

    getSelectedTidyRows() {
      if (!Array.isArray(this.selected) || this.selected.length === 0) {
        return [];
      }
      const selectedSet = new Set(this.selected);
      return this.vocaRowData.filter(row => selectedSet.has(row.id));
    },

    async saveTidyData() {
      const selectedRows = this.getSelectedTidyRows();
      if (!selectedRows.length) {
        return false;
      }

      this.tidySelectedExidTypes = selectedRows.map(row => ({
        exid: row.exid,
        type: row.type
      }));

      const requestData = {
        session: this.sessionUuid,
        data: selectedRows.map(row => ({
          input_id: row.input_id,
          exid: row.exid,
          preferred_name: row.preferred_name,
          id_info: row.id_info || '',
          type: row.type,
          synonyms: row.synonyms
        })),
        isexample: this.isUsingExampleData
      };

      try {
        const response = await axios.post('/api/edb/tidydata/', requestData, {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 300000
        });
        console.log('Tidy data saved:', response.data);
        return !!response.data?.success;
      } catch (error) {
        console.error('保存 tidy 数据失败:', error);
        alert('保存 tidy 数据失败: ' + error.message);
        return false;
      }
    },

    // Step 3 methods
    getDatabaseName(dbId) {
      const db = this.availableDatabases.find(d => d.id === dbId);
      return db ? db.name : dbId;
    },

    getDatabaseDescription(dbId) {
      const db = this.availableDatabases.find(d => d.id === dbId);
      return db ? db.description : '';
    },

    // 切换全选状态
    toggleSelectAll() {
      if (this.selectAllDatabases) {
        // 如果当前是全选状态，点击后取消全选
        this.selectedDatabases = [];
        this.selectAllDatabases = false;
      } else {
        // 如果当前不是全选状态，点击后全选
        this.selectedDatabases = this.availableDatabases.map(db => db.id);
        this.selectAllDatabases = true;
      }
    },

    // 切换单个数据库的选中状态
    toggleDatabaseSelection(dbId) {
      const index = this.selectedDatabases.indexOf(dbId);
      if (index > -1) {
        this.selectedDatabases.splice(index, 1);
      } else {
        this.selectedDatabases.push(dbId);
      }
      // 更新全选状态
      this.selectAllDatabases = this.selectedDatabases.length === this.availableDatabases.length;
    },

    previewDatabase(databaseId) {
      this.currentPreviewDatabase = databaseId;
      this.fetchBuildModelData(databaseId);
    },

    resolveBuildModelMode(databaseId) {
      return this.buildModelModes[databaseId] || '';
    },

    async fetchBuildModelData(databaseId) {
      this.previewLoaded = false;
      this.previewLoading = true;
      // 立即清空之前的数据，避免在新数据返回前显示旧数据
      this.previewHeaders = [];
      this.previewData = [];
      this.previewRequestId += 1;
      const requestId = this.previewRequestId;
      const mode = this.resolveBuildModelMode(databaseId);
      if (!mode) {
        this.previewHeaders = [];
        this.previewData = [];
        this.previewLoading = false;
        return;
      }

      if (!this.tidySelectedExidTypes.length) {
        this.previewHeaders = [];
        this.previewData = [];
        this.previewLoading = false;
        return;
      }

      try {
        const edges = await this.pollBuildModel({
          session: this.sessionUuid,
          mode,
          EXID: this.tidySelectedExidTypes,
          isexample: this.isUsingExampleData
        }, requestId);
        if (requestId !== this.previewRequestId) {
          return;
        }
        this.previewHeaders = this.buildPreviewHeaders(edges);
        this.previewData = edges;
        this.previewLoaded = true;
        this.previewLoading = false;
      } catch (error) {
        if (requestId !== this.previewRequestId) {
          return;
        }
        console.error('获取数据库预览失败:', error);
        alert('获取数据库预览失败: ' + error.message);
        this.previewHeaders = [];
        this.previewData = [];
        this.previewLoaded = false;
        this.previewLoading = false;
      }
    },

    buildPreviewHeaders(edges) {
      const baseKeys = ['source', 'target'];
      const firstEdge = edges && edges.length > 0 ? edges[0] : null;
      const extraKeys = firstEdge ? Object.keys(firstEdge).filter(key => !baseKeys.includes(key)) : [];
      const orderedKeys = baseKeys.concat(extraKeys);
      return orderedKeys.map(key => ({
        title: this.formatHeaderTitle(key),
        key,
        sortable: true
      }));
    },

    formatHeaderTitle(key) {
      if (!key) return '';
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    },

    isEllipsisField(key) {
      if (!key) return false;
      const normalized = String(key).trim().toLowerCase().replace(/_/g, ' ');
      const targets = new Set([
        'source preferred name',
        'target preferred name',
        'interaction'
      ]);
      return targets.has(normalized);
    },

    async pollBuildModel(payload, requestId) {
      const maxAttempts = 150;
      const intervalMs = 5000;
      let attempt = 0;

      while (attempt < maxAttempts) {
        if (requestId !== this.previewRequestId) {
          return [];
        }
        const response = await axios.post('/api/edb/build-model/', payload, {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 300000
        });

        const data = response.data || {};
        if (data.status === 'running') {
          attempt += 1;
          await this.sleep(intervalMs);
          continue;
        }

        return data.edges || [];
      }

      throw new Error('查询超时，请稍后重试');
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    async pollConvert(payload) {
      const maxAttempts = 150;
      const intervalMs = 5000;
      let attempt = 0;
      this.convertRequestId += 1;
      const requestId = this.convertRequestId;
      this.showLongProcessingAlert = false; // 重置提示状态

      // 使用 setTimeout 延迟显示提示（10秒后显示）
      const alertTimer = setTimeout(() => {
        if (requestId === this.convertRequestId) {
          this.showLongProcessingAlert = true;
        }
      }, 5000);

      try {
        while (attempt < maxAttempts) {
          if (requestId !== this.convertRequestId) {
            this.showLongProcessingAlert = false;
            return {};
          }
          const response = await axios.post('/api/edb/convert/', payload, {
            headers: {
              'Content-Type': 'application/json',
            },
            timeout: 300000
          });
          const data = response.data || {};
          if (data.status === 'running') {
            attempt += 1;
            await this.sleep(intervalMs);
            continue;
          }
          return data;
        }

        throw new Error('转换超时，请稍后重试');
      } finally {
        clearTimeout(alertTimer);
        this.showLongProcessingAlert = false; // 完成后隐藏提示
      }
    },

    // Step 4 methods
    downloadSelectedDatabase() {
      if (!this.selectedDatabase) return;
      
      const db = this.availableDatabases.find(d => d.id === this.selectedDatabase);
      if (!db) return;

      // Generate CSV content
      const headers = ['Database ID', 'Database Name', 'Description'];
      const row = [db.id, db.name, db.description];

      let csvContent = headers.join(',') + '\n';
      csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${db.id}_database.csv`;
      link.click();
      URL.revokeObjectURL(url);

      this.downloadSuccess = true;
      setTimeout(() => {
        this.downloadSuccess = false;
      }, 3000);
    },

    downloadPreviewData() {
      if (!this.previewData.length) return;

      // 设置默认文件名并打开弹窗
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const dbName = this.currentPreviewDatabaseName || 'database';
      this.previewDownloadFileName = `${dbName}_${timestamp}`;
      this.previewDownloadDialogVisible = true;
    },

    confirmPreviewDownload() {
      if (!this.previewDownloadFileName.trim()) {
        alert('Please enter a file name.');
        return;
      }

      if (!this.previewData.length) return;

      const headers = this.previewHeaders.map(header => header.key);
      const csvLines = [];
      csvLines.push(headers.join(','));

      this.previewData.forEach(row => {
        const line = headers.map(key => {
          const value = row[key] ?? '';
          const escaped = String(value).replace(/"/g, '""');
          return `"${escaped}"`;
        }).join(',');
        csvLines.push(line);
      });

      const csvContent = csvLines.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${this.previewDownloadFileName.trim()}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      this.previewDownloadDialogVisible = false;
    },

    downloadTidyData() {
      const selectedRows = this.getSelectedTidyRows();
      if (!selectedRows.length) return;

      // 设置默认文件名并打开弹窗
      const sessionStr = this.sessionUuid ? `_${this.sessionUuid.substring(0, 8)}` : '';
      this.tidyDownloadFileName = `tidy_data${sessionStr}`;
      this.tidyDownloadDialogVisible = true;
    },

    confirmTidyDownload() {
      if (!this.tidyDownloadFileName.trim()) {
        alert('Please enter a file name.');
        return;
      }

      const selectedRows = this.getSelectedTidyRows();
      if (!selectedRows.length) return;

      const headers = this.vocaHeaders.map(header => header.key);
      const csvLines = [];
      csvLines.push(headers.join(','));

      selectedRows.forEach(row => {
        const line = headers.map(key => {
          let value = row[key] ?? '';
          // Input ID 列使用 Excel 公式格式，防止自动转换日期
          if (key === 'input_id') {
            const escaped = String(value).replace(/"/g, '""');
            return `"=""${escaped}"""`;
          }
          const escaped = String(value).replace(/"/g, '""');
          return `"${escaped}"`;
        }).join(',');
        csvLines.push(line);
      });

      const csvContent = csvLines.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${this.tidyDownloadFileName.trim()}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      this.tidyDownloadDialogVisible = false;
    },

    async startSummaryBuild() {
      if (!this.tidySelectedExidTypes.length) {
        return;
      }
      // 如果没有选中任何数据库，不执行构建
      if (!this.selectedDatabases.length) {
        console.warn('没有选中任何数据库');
        return;
      }
      this.summaryRequestId += 1;
      const requestId = this.summaryRequestId;
      this.summaryLoading = true;
      this.summaryReady = false;
      this.summaryModes = {};

      try {
        const result = await this.pollSummary({
          session: this.sessionUuid,
          EXID: this.tidySelectedExidTypes,
          isexample: this.isUsingExampleData,
          modes: this.selectedModes  // 只构建选中的数据库对应的模式
        }, requestId);

        if (requestId !== this.summaryRequestId) {
          return;
        }
        this.summaryModes = result.modes || {};
        this.summaryReady = result.status === 'success';
      } catch (error) {
        if (requestId !== this.summaryRequestId) {
          return;
        }
        console.error('Summary 执行失败:', error);
        alert('Summary 执行失败: ' + error.message);
      } finally {
        if (requestId === this.summaryRequestId) {
          this.summaryLoading = false;
        }
      }
    },

    async pollSummary(payload, requestId) {
      const maxAttempts = 150;
      const intervalMs = 5000;
      let attempt = 0;

      while (attempt < maxAttempts) {
        if (requestId !== this.summaryRequestId) {
          return {};
        }
        const response = await axios.post('/api/edb/summary/', payload, {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 300000
        });
        const data = response.data || {};
        this.summaryModes = data.modes || {};

        if (data.status === 'success') {
          return data;
        }

        attempt += 1;
        await this.sleep(intervalMs);
      }

      throw new Error('Summary 查询超时，请稍后重试');
    },

    // 打开下载弹窗
    downloadSummaryZip() {
      if (!this.summaryReady) return;
      const downloadPath = this.isUsingExampleData ? 'example' : this.sessionUuid;
      this.downloadFileName = `edb_summary_${downloadPath}`;
      this.downloadDialogVisible = true;
    },

    // 确认下载
    async confirmDownload() {
      if (!this.downloadFileName.trim()) {
        alert('Please enter a file name.');
        return;
      }

      const downloadPath = this.isUsingExampleData ? 'example' : this.sessionUuid;
      const downloadUrl = `/api/session/Summary/download/${downloadPath}/edb/`;

      this.downloadLoading = true;
      try {
        const response = await axios.post(downloadUrl, {
          modes: this.selectedModes  // 只下载选中的模式
        }, {
          responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.downloadFileName.trim()}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.downloadDialogVisible = false;
        this.downloadSuccess = true;
        setTimeout(() => {
          this.downloadSuccess = false;
        }, 3000);
      } catch (error) {
        console.error('下载失败:', error);
        alert('下载失败: ' + (error.message || '未知错误'));
      } finally {
        this.downloadLoading = false;
      }
    },

    getModeStatus(dbId) {
      const mode = this.resolveBuildModelMode(dbId);
      if (!mode) return 'Pending';
      const status = this.summaryModes?.[mode]?.status || 'pending';
      return status.charAt(0).toUpperCase() + status.slice(1);
    },

    getSummaryStatusColor(status) {
      const s = status?.toLowerCase();
      if (s === 'success') return 'success';
      if (s === 'running') return 'info';
      if (s === 'failed') return 'error';
      return 'warning';
    },

    // 更新会话步骤
    async updateSessionStep(extra = {}) {
      try {
        console.log('更新会话步骤到:', this.stepId);

        // 检查是否有活动会话
        if (!this.sessionUuid) {
          console.warn('没有活动会话，跳过步骤更新');
          return;
        }

        // 确定是否是最后一步，如果是Summary页面（第4步），则设置status为1
        const isLastStep = this.stepId === 4;
        const status = isLastStep ? 1 : 0;

        await this.updateSession({
          session_step: this.stepId,
          status: status,
          ...extra
        });

        console.log(`会话步骤更新成功: step=${this.stepId}, status=${status}`);
      } catch (error) {
        console.error('更新会话步骤失败:', error);
        // 不阻断用户操作
      }
    },

    // Stepper navigation
    stepperPre() {
      if (this.stepId > 1) {
        this.stepId--;
      }
    },

    async stepperNext() {
      if (this.isLastStep) {
        this.closeCurrentTab();
        return;
      }
      // 设置loading为true，表示开始加载
      this.loading = true;
      console.log("Step: " + this.stepId);

      try {
        if (this.stepId === 1) {
          // 第一步：验证数据输入
          if (!this.hasValidInput) {
            this.loading = false;
            alert('请上传文件、输入词汇或选择示例数据后再继续');
            return;
          }

          // 设置上传状态
          this.fileUploadStatus = 'uploading';

          try {
            // 记录是否使用示例数据（后续步骤会用到）
            this.isUsingExampleData = this.useExampleData;

            // 检查是否有上传文件
            if (this.dataFileExisted && this.dataFiles && this.dataFiles.length > 0) {
              console.log('处理上传的文件数量:', this.dataFiles.length);

              if (!this.sessionUuid) {
                this.fileUploadStatus = 'error';
                this.convertResult = {
                  success: false,
                  message: '缺少会话信息，请刷新页面重试',
                  total_count: 0,
                  converted_count: 0
                };
                this.loading = false;
                return;
              }

              const formData = new FormData();
              formData.append('session', this.sessionUuid);
              this.dataFiles.forEach((file) => {
                formData.append('files', file);
              });

              console.log('发送文件上传请求，文件数量:', this.dataFiles.length);
              const uploadResponse = await axios.post('/api/session/uploadFile/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });

              console.log('文件上传响应:', uploadResponse.data);
              if (uploadResponse.data.success) {
                // 新流程：uploadFile 只返回 ids，需要再调用 convert 接口
                const extractedIds = uploadResponse.data.ids;
                console.log('从文件中提取的ID数量:', extractedIds?.length || 0);

                if (!extractedIds || extractedIds.length === 0) {
                  this.fileUploadStatus = 'error';
                  this.convertResult = {
                    success: false,
                    message: '文件中未找到有效的ID',
                    total_count: 0,
                    converted_count: 0
                  };
                  this.loading = false;
                  return;
                }

                // 调用 convert 接口进行ID转换
                const convertRequestData = {
                  ids: extractedIds,
                  session: this.sessionUuid,
                  isexample: false,
                  includingChemical: this.includeChemicalName
                };
                console.log('发送ID转换请求:', convertRequestData);

                const convertData = await this.pollConvert(convertRequestData);
                console.log('转换响应:', convertData);
                this.convertResult = convertData;
              } else {
                this.fileUploadStatus = 'error';
                this.convertResult = {
                  success: false,
                  message: uploadResponse.data.message || '文件上传失败',
                  total_count: 0,
                  converted_count: 0
                };
                this.loading = false;
                return;
              }
            } else if (this.userInputIds && this.userInputIds.trim()) {
              // 处理用户输入的词汇（包括示例数据填充的词汇）
              console.log('处理输入框中的词汇数据...');
              const ids = this.userInputIds.split('\n').map(id => id.trim()).filter(id => id.length > 0);
              console.log('解析后的词汇列表:', ids);

              if (ids.length === 0) {
                console.warn('没有找到有效的ID');
                this.fileUploadStatus = 'error';
                this.convertResult = {
                  success: false,
                  message: '没有找到有效的ID',
                  total_count: 0,
                  converted_count: 0
                };
                this.loading = false;
                return;
              }

              // 构造请求数据
              const requestData = {
                "ids": ids,
                "session": this.sessionUuid,
                "isexample": this.isUsingExampleData,
                includingChemical: this.includeChemicalName
              };

              console.log('发送ID转换请求:', requestData);

              const convertData = await this.pollConvert(requestData);
              console.log('转换响应:', convertData);
              this.convertResult = convertData;
            }

            if (this.convertResult && this.convertResult.success) {
              // 检查转换的数据条数
              if (this.convertResult.converted_count === 0) {
                this.loading = false;
                this.fileUploadStatus = 'error';
                alert('数据转换成功，但没有找到有效的ID。请检查输入的数据格式是否正确。');
                return;
              }

              console.log('转换成功，共转换', this.convertResult.converted_count, '个ID');

              // 加载转换后的数据到第二步的表格
              this.loadConvertedData();

              this.fileUploadStatus = 'success';
              this.stepId = 2;
              if (this.isUsingExampleData) {
                await this.updateSessionStep({ isExample: true });
              } else {
                await this.updateSessionStep();
              }
            } else {
              console.error('数据转换失败:', this.convertResult?.message || '未知错误');
              this.fileUploadStatus = 'error';
              alert('数据转换失败: ' + (this.convertResult?.message || '未知错误'));
            }

          } catch (error) {
            console.error('API调用失败:', error);
            this.fileUploadStatus = 'error';
            this.convertResult = {
              success: false,
              message: '请求失败: ' + error.message,
              total_count: 0,
              converted_count: 0
            };
            alert('数据转换失败: ' + error.message);
          }

        } else if (this.stepId === 2) {
          // Step 2 -> Step 3
          if (this.selected && this.selected.length > 0) {
            const saved = await this.saveTidyData();
            if (saved) {
              this.stepId = 3;
              await this.updateSessionStep();
            }
          }
        } else if (this.stepId === 3) {
          // Step 3 -> Step 4（允许不选数据库也能进入 Summary）
          this.stepId = 4;
          await this.updateSessionStep();
          this.startSummaryBuild();
        }
      } finally {
        this.loading = false;
      }
    },

    closeCurrentTab() {
      // window.close() 只能关闭由脚本打开的窗口，使用路由导航代替
      this.$router.push('/');
    },

    // 读取文件内容
    readFileContent(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsText(file);
      });
    },

    // 从文件内容中解析 IDs
    parseIdsFromContent(content, fileName) {
      const ids = [];
      const ext = fileName.toLowerCase().split('.').pop();

      if (ext === 'csv') {
        // CSV 文件：跳过第一行（表头），取第一列
        const lines = content.split('\n').filter(line => line.trim());
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split(',');
          if (cols[0] && cols[0].trim()) {
            ids.push(cols[0].trim().replace(/^["']|["']$/g, ''));
          }
        }
      } else {
        // TXT 或其他文件：每行一个 ID
        const lines = content.split('\n').filter(line => line.trim());
        for (const line of lines) {
          ids.push(line.trim());
        }
      }

      return ids;
    },

    // 加载转换后的数据到表格
    loadConvertedData() {
      console.log('开始加载转换数据...');
      console.log('convertResult:', this.convertResult);

      if (!this.convertResult || !this.convertResult.data) {
        console.error('没有转换数据可用');
        return;
      }

      const convertedData = this.convertResult.data;
      console.log('转换数据:', convertedData);

      // 清空现有数据
      this.vocaRowData = [];

      // 处理转换后的数据
      if (convertedData && convertedData.length > 0) {
        // 为每条数据添加唯一 id
        this.vocaRowData = convertedData.map((item, index) => ({
          id: `row_${index}`,
          input_id: item.input_id || '',
          exid: item.exid || '',
          preferred_name: item.preferred_name || '',
          type: item.type || '',
          synonyms: item.synonyms || ''
        }));

        // 默认选中所有有效数据（exid 非空的数据）
        this.selected = this.vocaRowData
          .filter(item => item.exid && item.exid !== 'N/A')
          .map(item => item.id);

        console.log('加载完成，共', this.vocaRowData.length, '条数据');
        console.log('默认选中', this.selected.length, '条数据');
      }
    }
  }
};
</script>

<style scoped>
.separator-text {
  color: black;
  font-size: 18px;
  font-weight: 500;
  white-space: nowrap;
}

.file-info-section .v-card {
  border-radius: 8px;
  transition: all 0.3s ease;
}

.file-item {
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.file-item:not(:last-child) {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 12px;
  margin-bottom: 12px;
}

.tidy-data-table {
  border: 1px solid #212121;
  border-radius: 4px;
}

.data-format-content ul,
.data-format-content ol {
  padding-left: 1.2em;
  margin-left: 0;
}

.data-format-content li {
  padding-left: 0.3em;
}

.cell-ellipsis {
  display: inline-block;
  max-width: 220px;
  width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
}

.sort-icon {
  margin-left: 4px;
}

::v-deep(.v-stepper-item__title) {
  white-space: nowrap;
}

/* Tidy表格列名不换行，超出显示省略号 */
.tidy-data-table ::v-deep(th) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.tidy-data-table ::v-deep(th .d-flex) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tidy-data-table ::v-deep(th span) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stepper-actions {
  margin-top: 0;
}

.step-content {
  padding: 3px 16px 16px 16px;
  display: flex;
  gap: 8px;
}

mark {
  background-color: yellow;
  padding: 0 2px;
}

.summary-btn {
  min-width: 185px !important;
}

.summary-list ::v-deep(.v-list-item) {
  padding-right: 0 !important;
}

.summary-list ::v-deep(.v-list-item__append) {
  margin-right: 0 !important;
}

.database-list-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.database-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.select-all-item {
  cursor: pointer;
  background-color: rgba(var(--v-theme-primary), 0.05);
  transition: background-color 0.2s;
}

.select-all-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.1);
}

.preview-table {
  font-size: 0.875rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
}

/* Preview表格列名不换行 */
.preview-table ::v-deep(th) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-table ::v-deep(th .d-flex) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-table ::v-deep(th span) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

::v-deep(.preview-table .v-data-table__wrapper) {
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
}

/* 显式显示水平滚动条 */
.preview-table ::v-deep(.v-data-table__wrapper) {
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: #888 #f1f1f1; /* Firefox */
}

.preview-table ::v-deep(.v-data-table__wrapper)::-webkit-scrollbar {
  width: 8px;
  height: 8px;
  display: block;
}

.preview-table ::v-deep(.v-data-table__wrapper)::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.preview-table ::v-deep(.v-data-table__wrapper)::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.preview-table ::v-deep(.v-data-table__wrapper)::-webkit-scrollbar-thumb:hover {
  background: #555;
}

::v-deep(.preview-table .v-data-table-footer) {
  flex-shrink: 0;
}

.square-close-btn {
  border-radius: 4px !important;
  min-width: 32px !important;
  width: 32px !important;
  height: 32px !important;
  padding: 0 !important;
}

::v-deep th, ::v-deep td {
  border: 1px solid #cccccc;
}

::v-deep(.v-data-table) { 
  overflow: hidden; 
}

::v-deep(.v-data-table__wrapper) {
  overflow-y: auto;
  max-height: 400px;
  height: 400px;
}

::v-deep(.v-data-table__thead) {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: white;
}
</style>
